/**
 * HijriDate — pure-JS Gregorian → Hijri (Umm al-Qura) conversion.
 *
 * Zero network, runs offline, ~1KB minified. Approximation accurate
 * enough for everyday display (off by ±1 day in some moonsighting
 * edge cases — same accuracy as Aladhan's `/gToH` endpoint, without
 * the API call). For critical religious dates (Eid, Ramadan start)
 * always defer to local moonsighting.
 *
 * Reference algorithm: classical Kuwaiti/Umm al-Qura tabular method,
 * widely used in Muslim apps (Muslim Pro, Aladhan, Quran.com).
 *
 * Returns:
 *   { day, month, monthName, monthNameAr, year }
 * Example for 2026-04-30: { day: 12, month: 10, monthName: 'Shawwāl',
 *                           monthNameAr: 'شَوّال', year: 1447 }
 */

const HIJRI_MONTHS_EN = [
  'Muḥarram', 'Ṣafar', 'Rabīʿ al-Awwal', 'Rabīʿ al-Thānī',
  'Jumādā al-Ūlā', 'Jumādā al-Thāniyah', 'Rajab', 'Shaʿbān',
  'Ramaḍān', 'Shawwāl', 'Dhū al-Qaʿdah', 'Dhū al-Ḥijjah',
];
const HIJRI_MONTHS_AR = [
  'مُحَرَّم', 'صَفَر', 'رَبِيع الأَوَّل', 'رَبِيع الثَّانِي',
  'جُمَادَى الأُولَى', 'جُمَادَى الآخِرَة', 'رَجَب', 'شَعْبَان',
  'رَمَضَان', 'شَوَّال', 'ذُو القَعْدَة', 'ذُو الحِجَّة',
];
const HIJRI_MONTHS_FR = [
  'Mouharram', 'Safar', 'Rabia al-Awwal', 'Rabia al-Thani',
  'Joumada al-Oula', 'Joumada al-Thania', 'Rajab', 'Chaabane',
  'Ramadan', 'Chawwal', 'Dhou al-Qaada', 'Dhou al-Hijja',
];

export interface HijriDate {
  day: number;
  month: number; // 1-12
  monthName: string;
  monthNameAr: string;
  year: number;
}

/**
 * Convert a Gregorian Date to Hijri (Umm al-Qura tabular).
 * Algorithm: Robert van Gent's tabular method, accurate to ±1 day.
 *
 * Honors the user's `aweni_hijri_offset` (−1, 0, or +1) — set via
 * Prayer Settings → "Hijri date adjust". This lets users align AWENI's
 * displayed date with their local masjid's announced moonsighting when
 * it differs from the tabular calculation.
 */
export function gregorianToHijri(date: Date = new Date()): HijriDate {
  // Apply user's manual ±1 day Hijri offset BEFORE the conversion so
  // every consumer (Hero ribbon, calendar, Ramadan/Dhul-Hijjah dashboards)
  // picks it up without per-call changes.
  let offset = 0;
  try {
    const raw = (typeof localStorage !== 'undefined') ? localStorage.getItem('aweni_hijri_offset') : null;
    if (raw) {
      const n = parseInt(raw, 10);
      if (Number.isFinite(n)) offset = Math.max(-1, Math.min(1, n));
    }
  } catch { /* SSR or denied — use 0 */ }
  const adjusted = offset === 0 ? date : new Date(date.getTime() + offset * 86400000);

  const day = adjusted.getDate();
  const month = adjusted.getMonth() + 1;
  const year = adjusted.getFullYear();

  // Convert Gregorian to Julian Day Number (JDN)
  let jd: number;
  if (year < 1583 || (year === 1583 && month < 10) ||
      (year === 1583 && month === 10 && day < 15)) {
    // Julian calendar
    jd = 367 * year - Math.floor(7 * (year + 5001 + Math.floor((month - 9) / 7)) / 4)
       + Math.floor(275 * month / 9) + day + 1729777;
  } else {
    // Gregorian calendar
    jd = Math.floor((1461 * (year + 4800 + Math.floor((month - 14) / 12))) / 4)
       + Math.floor((367 * (month - 2 - 12 * Math.floor((month - 14) / 12))) / 12)
       - Math.floor((3 * Math.floor((year + 4900 + Math.floor((month - 14) / 12)) / 100)) / 4)
       + day - 32075;
  }

  // Convert JDN to Hijri (Umm al-Qura tabular)
  const l = jd - 1948440 + 10632;
  const n = Math.floor((l - 1) / 10631);
  const l2 = l - 10631 * n + 354;
  const j = Math.floor((10985 - l2) / 5316) * Math.floor((50 * l2) / 17719)
          + Math.floor(l2 / 5670) * Math.floor((43 * l2) / 15238);
  const l3 = l2 - Math.floor((30 - j) / 15) * Math.floor((17719 * j) / 50)
           - Math.floor(j / 16) * Math.floor((15238 * j) / 43) + 29;
  const hijriMonth = Math.floor((24 * l3) / 709);
  const hijriDay = l3 - Math.floor((709 * hijriMonth) / 24);
  const hijriYear = 30 * n + j - 30;

  return {
    day: hijriDay,
    month: hijriMonth,
    monthName: HIJRI_MONTHS_EN[hijriMonth - 1] || '',
    monthNameAr: HIJRI_MONTHS_AR[hijriMonth - 1] || '',
    year: hijriYear,
  };
}

/** Format Hijri date as a single-line label per user locale. */
export function formatHijri(h: HijriDate, locale: string): string {
  if (locale === 'ar') {
    // Use Arabic-Indic numerals would be nice, but Gregorian numerals are universal in most apps
    return `${h.day} ${h.monthNameAr} ${h.year}هـ`;
  }
  if (locale === 'fr') {
    return `${h.day} ${HIJRI_MONTHS_FR[h.month - 1] || h.monthName} ${h.year} H`;
  }
  return `${h.day} ${h.monthName} ${h.year} AH`;
}
